import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import GlobalNav from '../components/navigation/GlobalNav';
import SecondaryNav from '../components/navigation/SecondaryNav';
import EmergencyContactBanner from '../components/navigation/EmergencyContactBanner';
import OutstandingBalanceBanner from '../components/balance/OutstandingBalanceBanner';
import PushNavigationListener from '../components/navigation/PushNavigationListener';
import unifiedApi from '../utils/unifiedApi';

// Caching functions for breadcrumb names
const cacheKey = (type, id) => `breadcrumb_${type}_${id}`;
const getCached = (type, id) => {
  try {
    return sessionStorage.getItem(cacheKey(type, id)) || '';
  } catch {
    return '';
  }
};
const setCached = (type, id, name) => {
  try {
    sessionStorage.setItem(cacheKey(type, id), name);
  } catch {}
};

const Layout = ({ children }) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Check if we're in admin mode
  const isAdminMode = location.pathname.startsWith('/admin');
  // Chat fills the viewport below the nav and scrolls internally (no footer).
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/');

  // Extract IDs from pathname (useParams may not work in Layout for nested routes)
  const pathOrgId = location.pathname.match(/\/organization\/(\d+)/)?.[1];
  const pathLeagueId = location.pathname.match(/\/league\/(\d+)/)?.[1];
  const pathTeamId = location.pathname.match(/\/team\/(\d+)/)?.[1];
  const pathPlayerId = location.pathname.match(/\/player\/(\d+)/)?.[1];

  const orgId = params.orgId || pathOrgId;
  const leagueId = params.leagueId || pathLeagueId;
  const teamId = params.teamId || pathTeamId;
  const playerId = params.playerId || pathPlayerId;

  // State for breadcrumb names
  const [tenantName, setTenantName] = useState(getCached('tenant', orgId));
  const [leagueName, setLeagueName] = useState(getCached('league', leagueId));
  const [teamName, setTeamName] = useState(getCached('team', teamId));
  const [playerName, setPlayerName] = useState(getCached('player', playerId));
  const [currentUserTenantId, setCurrentUserTenantId] = useState(null);



  // Clear names when IDs change
  useEffect(() => {
    const cachedName = getCached('tenant', orgId);
    setTenantName(cachedName || '');
  }, [orgId]);

  useEffect(() => {
    const cachedName = getCached('league', leagueId);
    setLeagueName(cachedName || '');
  }, [leagueId]);

  useEffect(() => {
    const cachedName = getCached('team', teamId);
    setTeamName(cachedName || '');
  }, [teamId]);

  useEffect(() => {
    const cachedName = getCached('player', playerId);
    setPlayerName(cachedName || '');
  }, [playerId]);

  // Fetch current user's userTenantId
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (isAuthenticated && !currentUserTenantId) {
        try {
          const userData = await unifiedApi.get('/api/me');
          if (userData?.userTenantId) {
            setCurrentUserTenantId(userData.userTenantId);
          }
        } catch (error) {
          console.warn('Failed to load current user data:', error);
        }
      }
    };
    fetchCurrentUser();
  }, [isAuthenticated, currentUserTenantId]);

  // Fetch names from API for breadcrumbs
  useEffect(() => {
    const loadData = async () => {
      if (!orgId && !leagueId && !teamId && !playerId) return;

      try {
        if (orgId && !tenantName) {
          try {
            const data = await unifiedApi.get('/api/Tenant/current');
            if (data?.name) {
              setTenantName(data.name);
              setCached('tenant', orgId, data.name);
            }
          } catch (error) {
            console.warn('Failed to load tenant name:', error);
          }
        }

        if (leagueId && !leagueName) {
          try {
            const data = await unifiedApi.get(`/api/leagues/${leagueId}`);
            if (data?.name) {
              setLeagueName(data.name);
              setCached('league', leagueId, data.name);
            }
          } catch (error) {
            console.warn('Failed to load league name:', error);
          }
        }

        if (teamId && !teamName) {
          try {
            const data = await unifiedApi.get(`/api/teams/${teamId}`);
            if (data?.name) {
              setTeamName(data.name);
              setCached('team', teamId, data.name);
            }
          } catch (error) {
            console.warn('Failed to load team name:', error);
          }
        }

        if (playerId && !playerName) {
          try {
            const playerData = await unifiedApi.getPlayerProfile(playerId);
            if (playerData) {
              const fullName = `${playerData.firstName || ''} ${playerData.lastName || ''}`.trim();
              const displayName = fullName || `Player ${playerId}`;
              setPlayerName(displayName);
              setCached('player', playerId, displayName);
            } else {
              setPlayerName(`Player ${playerId}`);
            }
          } catch (error) {
            console.warn('Failed to load player name:', error);
            setPlayerName(`Player ${playerId}`);
          }
        }
      } catch (error) {
        console.warn('Error loading breadcrumb data:', error);
      }
    };

    loadData();
  }, [orgId, leagueId, teamId, playerId, tenantName, leagueName, teamName, playerName]);

  const handleAvailabilityClick = async () => {
    if (availabilityLoading) return;
    setAvailabilityLoading(true);
    try {
      const data = await unifiedApi.getMyAvailabilityToken();
      if (data?.type === 'player' && data?.token) {
        navigate(`/player-availability/${data.token}`);
      } else if (data?.type === 'spare' && data?.token) {
        navigate(`/spare/availability/${data.token}`);
      }
    } catch (error) {
      console.error('Error fetching availability info:', error);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...(isChatRoute
          ? { height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden' }
          : { minHeight: '100vh' }),
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PushNavigationListener />

      {/* Global Navigation (Primary AppBar) */}
      <GlobalNav
        currentUserTenantId={currentUserTenantId}
        availabilityLoading={availabilityLoading}
        onAvailabilityClick={handleAvailabilityClick}
      />

      {/* Secondary Navigation (Contextual League Tabs) */}
      <SecondaryNav />

      <OutstandingBalanceBanner />

      <EmergencyContactBanner />

      {/* Breadcrumb Navigation */}
      {!location.pathname.includes('/spares') && orgId && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: '#f8f9fa',
            py: 1,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ py: 1 }}>
              {(() => {
                const pathSegments = location.pathname.split('/').filter(Boolean);
                const breadcrumbItems = [];
                const isAdminPath = location.pathname.includes('/admin/');

                breadcrumbItems.push({
                  label: tenantName || `Org ${orgId}`,
                  link: isAdminPath ? `/admin/organization/${orgId}` : `/organization/${orgId}`,
                  isLast: false,
                });

                if (pathSegments.includes('leagues')) {
                  breadcrumbItems.push({
                    label: 'Leagues',
                    link: isAdminPath ? `/admin/organization/${orgId}/leagues` : `/organization/${orgId}/leagues`,
                    isLast: true,
                  });
                }

                if (pathSegments.includes('teams') && !isAdminPath) {
                  breadcrumbItems.push({
                    label: 'Teams',
                    link: `/organization/${orgId}/teams`,
                    isLast: true,
                  });
                }

                if (pathSegments.includes('players') && !isAdminPath) {
                  breadcrumbItems.push({
                    label: 'Players',
                    link: `/organization/${orgId}/players`,
                    isLast: true,
                  });
                }

                if (pathSegments.includes('spares') && !isAdminPath) {
                  breadcrumbItems.push({
                    label: 'Spares',
                    link: `/organization/${orgId}/spares`,
                    isLast: true,
                  });
                }

                if (pathSegments.includes('venues') && !isAdminPath) {
                  breadcrumbItems.push({
                    label: 'Venues',
                    link: `/organization/${orgId}/venues`,
                    isLast: true,
                  });
                }

                if (pathSegments.includes('members') && isAdminPath) {
                  breadcrumbItems.push({
                    label: 'Members',
                    link: `/admin/organization/${orgId}/members`,
                    isLast: true,
                  });
                }

                const leagueIndex = pathSegments.indexOf('league');
                if (leagueIndex !== -1 && pathSegments[leagueIndex + 1]) {
                  const currentLeagueId = pathSegments[leagueIndex + 1];
                  breadcrumbItems.push({
                    label: leagueName || `League ${currentLeagueId}`,
                    link: isAdminPath
                      ? `/admin/organization/${orgId}/league/${currentLeagueId}`
                      : `/organization/${orgId}/league/${currentLeagueId}`,
                    isLast: false,
                  });

                  if (isAdminPath) {
                    if (pathSegments.includes('teams') && pathSegments.indexOf('teams') > leagueIndex) {
                      breadcrumbItems.push({
                        label: 'Teams',
                        link: `/admin/organization/${orgId}/league/${currentLeagueId}/teams`,
                        isLast: true,
                      });
                    }

                    if (pathSegments.includes('members') && pathSegments.indexOf('members') > leagueIndex) {
                      breadcrumbItems.push({
                        label: 'Members',
                        link: `/admin/organization/${orgId}/league/${currentLeagueId}/members`,
                        isLast: true,
                      });
                    }

                    if (pathSegments.includes('availability')) {
                      breadcrumbItems.push({
                        label: 'Availability',
                        link: `/admin/leagues/${currentLeagueId}/availability`,
                        isLast: true,
                      });
                    }
                  }
                }

                const teamIndex = pathSegments.indexOf('team');
                if (teamIndex !== -1 && pathSegments[teamIndex + 1]) {
                  const currentTeamId = pathSegments[teamIndex + 1];
                  const currentLeagueId = pathSegments[leagueIndex + 1];
                  breadcrumbItems.push({
                    label: teamName || `Team ${currentTeamId}`,
                    link: isAdminPath
                      ? `/admin/organization/${orgId}/league/${currentLeagueId}/team/${currentTeamId}`
                      : `/organization/${orgId}/league/${currentLeagueId}/team/${currentTeamId}`,
                    isLast: false,
                  });
                }

                const playerIndex = pathSegments.indexOf('player');
                if (playerIndex !== -1 && pathSegments[playerIndex + 1]) {
                  breadcrumbItems.push({
                    label: playerName || `Player ${pathSegments[playerIndex + 1]}`,
                    link: null,
                    isLast: true,
                  });
                }

                if (breadcrumbItems.length > 0) {
                  breadcrumbItems[breadcrumbItems.length - 1].isLast = true;
                }

                return (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                    }}
                  >
                    {breadcrumbItems.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.isLast || !item.link ? (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: 'text.primary', fontWeight: 400 }}
                          >
                            {item.label}
                          </Typography>
                        ) : (
                          <Typography
                            component={Link}
                            to={item.link}
                            variant="body2"
                            sx={{
                              textDecoration: 'none',
                              color: 'primary.main',
                              fontWeight: 400,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {item.label}
                          </Typography>
                        )}
                        {index < breadcrumbItems.length - 1 && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: 'text.secondary', userSelect: 'none', mx: 0.5 }}
                          >
                            ›
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Typography>
                );
              })()}
            </Box>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Container
        maxWidth={isAdminMode ? 'xl' : 'lg'}
        disableGutters={isChatRoute}
        sx={{
          py: isChatRoute ? { xs: 0, md: 2 } : { xs: 2, sm: 4 },
          px: isChatRoute ? { xs: 0, md: 2 } : { xs: 1, sm: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          ...(isChatRoute && { minHeight: 0 }),
        }}
      >
        {children}
      </Container>
      {!isChatRoute && <Footer />}
    </Box>
  );
};

export default Layout;